// NAS ジョブの表示状態（保存済みの履歴は書き換えず、表示だけを安全に導出する）。
// 失敗があるのに緑の「完了」を見せないための単一の情報源。
import { CircleNotch, CheckCircle, Warning, StopCircle, Pause, Clock, WarningCircle } from '@phosphor-icons/react';

export const NAS_STATE_STYLE = {
  queued: { color: 'text-zinc-600', bg: 'bg-zinc-50', border: 'border-zinc-200', labelJa: '待機中', labelEn: 'Queued', Icon: Clock },
  running: { color: 'text-sky-700', bg: 'bg-sky-50', border: 'border-sky-200', labelJa: '実行中', labelEn: 'Running', Icon: CircleNotch, spin: true },
  paused: { color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', labelJa: '一時停止中', labelEn: 'Paused', Icon: Pause },
  completed: { color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', labelJa: '完了', labelEn: 'Completed', Icon: CheckCircle },
  completed_with_warnings: { color: 'text-amber-800', bg: 'bg-amber-50', border: 'border-amber-300', labelJa: '完了（エラーあり）', labelEn: 'Completed with errors', Icon: WarningCircle },
  truncated: { color: 'text-amber-800', bg: 'bg-amber-50', border: 'border-amber-300', labelJa: '不完全（上限到達）', labelEn: 'Incomplete — limit reached', Icon: WarningCircle },
  cancelled: { color: 'text-zinc-600', bg: 'bg-zinc-50', border: 'border-zinc-300', labelJa: '中止', labelEn: 'Cancelled', Icon: StopCircle },
  failed: { color: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-200', labelJa: '失敗', labelEn: 'Failed', Icon: Warning },
};

/** 保存済みのプレビュージョブ → 表示状態 */
export function derivePreviewState(job) {
  if (!job) return { key: 'queued', ...NAS_STATE_STYLE.queued };
  const failures = Number(job.aggregate?.failures || 0);
  let key = job.status;
  if (job.status === 'completed' && failures > 0) key = 'completed_with_warnings';
  if (!NAS_STATE_STYLE[key]) key = 'queued';
  const st = NAS_STATE_STYLE[key];
  const cancelling = job.status === 'running' && job.cancellation_requested;
  return {
    key,
    ...st,
    labelJa: cancelling ? '中止要求中' : st.labelJa,
    labelEn: cancelling ? 'Cancellation requested' : st.labelEn,
  };
}

/** 保存済みのクロールジョブ（crawler_jobs）→ 表示状態。履歴は書き換えない。 */
export function deriveCrawlState(job) {
  if (!job) return { key: 'queued', ...NAS_STATE_STYLE.queued };
  const failed = Number(job.stats?.failed || 0);
  const indexed = Number(job.stats?.indexed || 0);
  const unchanged = Number(job.stats?.unchanged || 0);
  let key = { complete: 'completed', running: 'running', paused: 'paused', cancelled: 'cancelled', failed: 'failed' }[job.status] || 'queued';
  if (key === 'completed' && failed > 0) {
    key = (indexed + unchanged) === 0 ? 'failed' : 'completed_with_warnings';
  }
  const st = NAS_STATE_STYLE[key];
  const allFailed = job.status === 'complete' && failed > 0 && (indexed + unchanged) === 0;
  return {
    key,
    ...st,
    labelJa: allFailed ? '失敗（成功0件）' : st.labelJa,
    labelEn: allFailed ? 'Failed — nothing succeeded' : st.labelEn,
  };
}

export const shortId = (id) => (id ? String(id).slice(0, 8) : '—');

/** カーソル取り込みジョブ（v1.4.0 の公開ビュー）→ 表示状態。
 *
 * サーバー側の状態をそのまま尊重する。「完了」と「不完全（上限到達）」を
 * 混同させない。中止要求中は実行中とは別に見せる。 */
export function deriveIngestState(job) {
  if (!job) return { key: 'queued', ...NAS_STATE_STYLE.queued };
  const map = {
    queued: 'queued',
    running: 'running',
    paused: 'paused',
    complete: 'completed',
    truncated: 'truncated',
    cancelled: 'cancelled',
    failed: 'failed',
  };
  let key = map[job.status] || 'queued';
  const failed = Number(job.stats?.failed || 0);
  const ok = Number(job.stats?.indexed || 0) + Number(job.stats?.unchanged || 0);
  if (key === 'completed' && failed > 0) {
    key = ok === 0 ? 'failed' : 'completed_with_warnings';
  }
  const st = NAS_STATE_STYLE[key];
  if (job.is_active && job.cancel_requested) {
    return { key, ...st, labelJa: '中止要求中', labelEn: 'Cancellation requested' };
  }
  if (job.is_active && job.pause_requested) {
    return { key, ...st, labelJa: '一時停止を要求中', labelEn: 'Pause requested' };
  }
  if (key === 'paused' && job.can_resume) {
    return { key, ...st, labelJa: '一時停止中（再開できます）', labelEn: 'Paused — resumable' };
  }
  return { key, ...st };
}
